import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { JobEventSummary, RecipeEvent, RecipeEventSummary, RecipeEventType } from 'situanalytics-webapi-client';

/**
 * Time-based progress bar for a recipe, from queued to finished (or now, while running). A long
 * queue is shortened with a break so the render keeps most of the bar. File and error ticks sit
 * above the bar, job brackets below, and clicking either emits the recipe event id.
 */
@Component({
  selector: 'recipe-progress-bar',
  templateUrl: './recipe-progress-bar.component.html',
  styleUrls: ['./recipe-progress-bar.component.scss']
})
export class RecipeProgressBarComponent implements OnChanges {
  // Ticks closer than this (percent of the bar) merge, so each stays wide enough to hover.
  private readonly TickMergePercent = 0.75;
  private readonly MaxTooltipFileNames = 12;
  // Beyond this share of the bar, the queue segment is shortened and drawn with a break.
  private readonly MaxQueuePercent = 20;

  @Input() recipeEventSummary: RecipeEventSummary | undefined;
  @Output() eventSelected = new EventEmitter<string>();

  queuedLabel = '';
  statusLabel = '';
  countsLabel = '';
  hasStarted = false;
  segments: BarSegment[] = [];
  fileTicks: BarTick[] = [];
  errorTicks: BarTick[] = [];
  jobs: JobBracket[] = [];
  jobAreaHeight = 0;

  ngOnChanges(): void {
    this.build();
  }

  selectEvent(eventId: string | undefined, event?: MouseEvent) {
    event?.stopPropagation();
    if (eventId) {
      this.eventSelected.emit(eventId);
    }
  }

  private build() {
    const summary = this.recipeEventSummary;
    this.segments = [];
    this.fileTicks = [];
    this.errorTicks = [];
    this.jobs = [];
    this.jobAreaHeight = 0;
    this.countsLabel = this.getCountsLabel(summary);
    if (!summary) return;

    const events = (summary.recipeEvents ?? []).filter(e => !!e.dateTimeStamp);
    const start = summary.dateStarted ?? events.find(e => e.recipeEventType === RecipeEventType.JobStart)?.dateTimeStamp;
    const waitingSince = summary.dateQueued ?? summary.dateAdded;
    const now = new Date();

    this.hasStarted = !!start;
    if (!start) {
      this.queuedLabel = waitingSince ? `Waiting in queue for ${formatDuration(now.getTime() - waitingSince.getTime())}` : 'Waiting in queue';
      this.statusLabel = '';
      return;
    }

    this.queuedLabel = '';

    const finishedAt = summary.dateCompleted ?? summary.dateFailed ?? summary.dateCancelled;
    const end = finishedAt ?? now;
    this.statusLabel = `${finishedAt ? 'Took' : 'Running for'} ${formatDuration(end.getTime() - start.getTime())}`;

    // The queue gets its natural share of the bar, capped so a long wait can't squash the render.
    const queueMs = waitingSince && waitingSince < start ? start.getTime() - waitingSince.getTime() : 0;
    const renderMs = Math.max(end.getTime() - start.getTime(), 1);
    const naturalQueuePercent = (queueMs / (queueMs + renderMs)) * 100;
    const queuePercent = Math.min(naturalQueuePercent, this.MaxQueuePercent);

    const toPercent = (date: Date) => {
      const ms = date.getTime() - start.getTime();
      return ms < 0
        ? clamp(queuePercent + (ms / Math.max(queueMs, 1)) * queuePercent)
        : clamp(queuePercent + (ms / renderMs) * (100 - queuePercent));
    };

    this.segments = this.buildSegments(summary, start, end, !finishedAt, toPercent);
    if (queuePercent > 0) {
      this.segments.unshift({
        left: 0,
        width: queuePercent,
        cssClass: 'in-queue',
        running: false,
        broken: naturalQueuePercent > this.MaxQueuePercent,
        tooltip: `Queued ${formatDuration(queueMs)}${naturalQueuePercent > this.MaxQueuePercent ? ' (shortened to fit)' : ''}`,
      });
    }
    this.fileTicks = this.buildTicks(events.filter(e => e.recipeEventType === RecipeEventType.FileAdded && e.fileName), e => e.fileName!, toPercent);
    this.errorTicks = this.buildTicks(events.filter(e => e.recipeEventType === RecipeEventType.Error), e => describeError(e.event), toPercent);
    this.jobs = this.buildJobs(summary, events, end, toPercent);
    this.jobAreaHeight = this.jobs.reduce((height, job) => Math.max(height, job.top + JobRowHeight
      + job.stageLaneCount * StageRowHeight), 0);
  }

  // Each segment runs from the previous milestone to this one, so a missing milestone is skipped
  // rather than producing a negative width.
  private buildSegments(summary: RecipeEventSummary, start: Date, end: Date, running: boolean, toPercent: (d: Date) => number): BarSegment[] {
    const milestones: { date: Date | undefined, label: string, cssClass: string }[] = [
      { date: summary.dateFirstFileFound, label: 'First file found', cssClass: 'first-file-found' },
      { date: summary.dateFirstFileAdded, label: 'First file added', cssClass: 'first-file-added' },
      { date: summary.dateFloorPlansAdded, label: 'Floor plans added', cssClass: 'floorplan-files-added' },
      { date: summary.dateFirstPanoramaFileAdded, label: 'First visual file added', cssClass: 'first-panorama-added' },
    ];

    const reached = milestones
      .filter(m => !!m.date && m.date >= start && m.date <= end)
      .sort((a, b) => a.date!.getTime() - b.date!.getTime());

    const endLabel = running ? 'Running' : summary.dateCompleted ? 'Completed' : summary.dateFailed ? 'Failed' : 'Cancelled';
    const endClass = summary.dateFailed ? 'failed-stage' : summary.dateCancelled ? 'cancelled-stage' : 'at-renderer';
    reached.push({ date: end, label: endLabel, cssClass: endClass });

    const segments: BarSegment[] = [];
    let previous = start;
    for (const milestone of reached) {
      const left = toPercent(previous);
      const width = toPercent(milestone.date!) - left;
      if (width > 0) {
        segments.push({
          left,
          width,
          cssClass: milestone.cssClass,
          running: running && milestone.date === end,
          broken: false,
          tooltip: `${milestone.label} after ${formatDuration(milestone.date!.getTime() - start.getTime())} (this stage ${formatDuration(milestone.date!.getTime() - previous.getTime())})`,
        });
      }
      previous = milestone.date!;
    }
    return segments;
  }

  private buildTicks(events: RecipeEvent[], describe: (e: RecipeEvent) => string, toPercent: (d: Date) => number): BarTick[] {
    const ticks: BarTick[] = [];
    for (const event of events) {
      const position = toPercent(event.dateTimeStamp!);
      const last = ticks[ticks.length - 1];
      if (last && position - last.position < this.TickMergePercent) {
        last.events.push(event);
      } else {
        ticks.push({ position, events: [event], tooltip: '' });
      }
    }

    for (const tick of ticks) {
      const names = tick.events.map(describe);
      const shown = names.slice(0, this.MaxTooltipFileNames);
      if (names.length > shown.length) {
        shown.push(`+${names.length - shown.length} more`);
      }
      tick.tooltip = shown.join('\n');
    }
    return ticks;
  }

  private buildJobs(summary: RecipeEventSummary, events: RecipeEvent[], axisEnd: Date, toPercent: (d: Date) => number): JobBracket[] {
    const jobIds = new Set<string>();
    events.filter(e => e.jobArtifactId && (e.recipeEventType === RecipeEventType.JobStart || e.recipeEventType === RecipeEventType.JobEnd))
      .forEach(e => jobIds.add(e.jobArtifactId!));
    (summary.jobEventSummaries ?? []).filter(j => j.jobArtifactId).forEach(j => jobIds.add(j.jobArtifactId!));

    const started: { jobEvents: RecipeEvent[], startEvent?: RecipeEvent, endEvent?: RecipeEvent, jobSummary?: JobEventSummary, startedAt: Date }[] = [];
    jobIds.forEach(jobId => {
      const jobEvents = events.filter(e => e.jobArtifactId === jobId);
      const startEvent = jobEvents.find(e => e.recipeEventType === RecipeEventType.JobStart);
      const endEvent = [...jobEvents].reverse().find(e => e.recipeEventType === RecipeEventType.JobEnd);
      const jobSummary = (summary.jobEventSummaries ?? []).find(j => j.jobArtifactId === jobId);
      const startedAt = startEvent?.dateTimeStamp ?? jobSummary?.dateStarted;
      if (startedAt) {
        started.push({ jobEvents, startEvent, endEvent, jobSummary, startedAt });
      }
    });
    // In start order, so a job with no text is numbered by when it started.
    started.sort((a, b) => a.startedAt.getTime() - b.startedAt.getTime());

    const jobs: JobBracket[] = started.map(({ jobEvents, startEvent, endEvent, jobSummary, startedAt }, index) => {
      const finishedAt = endEvent?.dateTimeStamp ?? jobSummary?.dateCompleted ?? jobSummary?.dateFailed;
      const failed = !!jobSummary?.dateFailed;

      const left = toPercent(startedAt);
      const name = startEvent?.event || endEvent?.event || `Job ${index + 1}`;
      const stages = this.buildStages(jobEvents, startedAt, finishedAt, failed, jobSummary?.errorMessage, axisEnd, toPercent);
      return {
        name,
        left,
        width: Math.max(toPercent(finishedAt ?? axisEnd) - left, 0),
        lane: 0,
        top: 0,
        open: !finishedAt,
        failed,
        startEventId: startEvent?.id,
        endEventId: endEvent?.id,
        tooltip: this.getJobTooltip(name, endEvent?.event, startedAt, finishedAt, jobSummary),
        stages,
        stageLaneCount: assignLanes(stages),
      };
    });

    // Overlapping jobs stack instead of drawing over each other, and each lane is as tall as the
    // most stage rows any of its jobs needs.
    const laneCount = assignLanes(jobs);
    const laneHeights: number[] = [];
    for (let lane = 0; lane < laneCount; lane++) {
      const stageRows = jobs.filter(j => j.lane === lane).reduce((rows, j) => Math.max(rows, j.stageLaneCount), 0);
      laneHeights.push(JobRowHeight + stageRows * StageRowHeight);
    }
    for (const job of jobs) {
      job.top = laneHeights.slice(0, job.lane).reduce((sum, height) => sum + height, 0);
      for (const stage of job.stages) {
        stage.top = job.top + JobRowHeight + stage.lane * StageRowHeight;
      }
    }
    return jobs;
  }

  // Each StageStart pairs with the next StageEnd for the same software, so software that runs twice
  // in one job gives two stages. A stage the job finished without ending closes with the job, and
  // is the one that failed if the job did.
  private buildStages(jobEvents: RecipeEvent[], jobStartedAt: Date, jobFinishedAt: Date | undefined, jobFailed: boolean,
    jobError: string | undefined, axisEnd: Date, toPercent: (d: Date) => number): Bracket[] {
    const stageEvents = jobEvents
      .filter(e => e.recipeEventType === RecipeEventType.StageStart || e.recipeEventType === RecipeEventType.StageEnd)
      .sort((a, b) => a.dateTimeStamp!.getTime() - b.dateTimeStamp!.getTime());

    const pairs: { start: RecipeEvent, end?: RecipeEvent }[] = [];
    for (const e of stageEvents) {
      if (e.recipeEventType === RecipeEventType.StageStart) {
        pairs.push({ start: e });
      } else {
        const pair = pairs.find(p => !p.end && p.start.software === e.software);
        if (pair) {
          pair.end = e;
        }
      }
    }

    return pairs.map(({ start, end }, index) => {
      const startedAt = start.dateTimeStamp!;
      const finishedAt = end?.dateTimeStamp ?? jobFinishedAt;
      const failed = !end && jobFailed;
      const name = stageText(start) || stageText(end) || `Stage ${index + 1}`;
      const endText = stageText(end);

      const lines = [name];
      if (start.software && start.software !== name) {
        lines.push(start.software);
      }
      lines.push(finishedAt
        ? `${failed ? 'Failed after' : 'Took'} ${formatDuration(finishedAt.getTime() - startedAt.getTime())}`
        : `Running for ${formatDuration(Date.now() - startedAt.getTime())}`);
      lines.push(`Started ${formatDuration(startedAt.getTime() - jobStartedAt.getTime())} into job`);
      if (failed && jobError) {
        lines.push(describeError(jobError));
      }
      if (endText && endText !== name) {
        lines.push(endText);
      }

      const left = toPercent(startedAt);
      return {
        name,
        left,
        width: Math.max(toPercent(finishedAt ?? axisEnd) - left, 0),
        lane: 0,
        top: 0,
        open: !finishedAt,
        failed,
        startEventId: start.id,
        endEventId: end?.id,
        tooltip: lines.join('\n'),
      };
    });
  }

  // Stage detail is left to the stage brackets.
  private getJobTooltip(name: string, endText: string | undefined, startedAt: Date, finishedAt: Date | undefined, jobSummary: JobEventSummary | undefined): string {
    const lines = [name];
    lines.push(finishedAt
      ? `${jobSummary?.dateFailed ? 'Failed after' : 'Took'} ${formatDuration(finishedAt.getTime() - startedAt.getTime())}`
      : `Running for ${formatDuration(Date.now() - startedAt.getTime())}`);

    if (jobSummary?.totalNumberOfStages) {
      lines.push(`Stages: ${jobSummary.numberOfStagesCompleted ?? 0} / ${jobSummary.totalNumberOfStages}`);
    }
    if (jobSummary?.numberOfFilesExpected) {
      lines.push(`Files: ${jobSummary.numberOfFilesRecieved ?? 0} / ${jobSummary.numberOfFilesExpected}`);
    }
    if (endText && endText !== name) {
      lines.push(endText);
    }
    if (jobSummary?.errorMessage) {
      lines.push(describeError(jobSummary.errorMessage));
    }
    return lines.join('\n');
  }

  private getCountsLabel(summary: RecipeEventSummary | undefined): string {
    if (!summary) return '';
    const parts: string[] = [];
    if (summary.numberOfFilesExpected) {
      parts.push(`${summary.numberOfFilesRecieved ?? 0} / ${summary.numberOfFilesExpected} files`);
    }
    if (summary.totalNumberOfJobs) {
      parts.push(`${summary.numberOfJobsCompleted ?? 0} / ${summary.totalNumberOfJobs} jobs`);
    }
    return parts.join(' · ');
  }
}

interface BarSegment {
  left: number;
  width: number;
  cssClass: string;
  running: boolean;
  broken: boolean;
  tooltip: string;
}

interface BarTick {
  position: number;
  events: RecipeEvent[];
  tooltip: string;
}

interface Bracket {
  name: string;
  left: number;
  width: number;
  lane: number;
  top: number;
  open: boolean;
  failed: boolean;
  startEventId?: string;
  endEventId?: string;
  tooltip: string;
}

interface JobBracket extends Bracket {
  stages: Bracket[];
  stageLaneCount: number;
}

const JobRowHeight = 30;
const StageRowHeight = 26;

function clamp(percent: number): number {
  return Math.min(Math.max(percent, 0), 100);
}

// Greedy assignment so overlapping brackets stack instead of drawing over each other. Sorts the
// brackets by start and returns the number of lanes used.
function assignLanes(brackets: Bracket[]): number {
  brackets.sort((a, b) => a.left - b.left);
  const laneEnds: number[] = [];
  for (const bracket of brackets) {
    let lane = laneEnds.findIndex(laneEnd => laneEnd <= bracket.left);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(0);
    }
    bracket.lane = lane;
    laneEnds[lane] = bracket.left + bracket.width;
  }
  return laneEnds.length;
}

// What the timeline shows for a stage: its software, else its own text.
function stageText(event: RecipeEvent | undefined): string {
  return lastKeySegment(event?.software) || event?.event || '';
}

// Long messages are left to the timeline row, which the tick or bracket scrolls to.
const MaxTooltipErrorLength = 40;

function describeError(message: string | undefined): string {
  return message && message.length <= MaxTooltipErrorLength ? `Error: ${message}` : 'There was an error';
}

function lastKeySegment(key: string | undefined): string {
  return key?.split(/[\\/]/).filter(s => !!s).pop() ?? '';
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.max(Math.round(ms / 1000), 0);
  if (totalSeconds < 1) return '<1s';

  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days) return `${days}d ${hours}h`;
  if (hours) return `${hours}h ${minutes}m`;
  if (minutes) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}
